import { useEffect, useCallback } from "react";
import {
  type RouteComponentProps,
  useHistory,
  useLocation,
} from "react-router-dom";
import URI from "urijs";
import {
  ActionType,
  UserLanguage,
  type MctYearPartFlag,
  ManagedQueryParameters,
} from "../@types";
import {
  getYearPartInfoFromYearPart,
  useCurrentPlanYear,
  useYearPart,
} from "./yearFlagHelpers";
import { useAppContext } from "./context-hooks/useAppContext";
import { parseSearchParams } from "./objectUtilities";
import { getShouldUpdateSearchYear } from "./yearHelpers";
import {
  getLanguage as getCmsdsLanguage,
  type Language as CmsdsLanguage,
  setLanguage as setCmsdsLanguage,
} from "@cmsgov/design-system";

/**
 * Manages setting and updating both year and language in the URL and application
 * state, keeping them in sync and ensuring that the year is valid for the currently
 * set year part (evaluated based on the current plan year)
 */
export const useQueryParams = (): void => {
  const {
    state: { language: languageInAppState, year: yearInAppState },
    dispatch,
  } = useAppContext();
  const location = useLocation();
  const history = useHistory();
  const { yearPart } = useYearPart();
  const { hash, search } = location;
  const mctCurrentPlanYear = useCurrentPlanYear();

  /**
   * Updates the year in URL and/or app state
   * Also sets the language in the URL when updating, so it's not wiped out
   */
  const handleAppYear = useCallback(
    ({
      location,
      mctCurrentPlanYear,
      yearPart,
    }: {
      location: RouteComponentProps["location"];
      mctCurrentPlanYear: number;
      yearPart: MctYearPartFlag;
    }): void => {
      const uri = new URI(location.search);
      const { lang: languageInUrl, year: yearInUrl } = parseSearchParams(
        uri.search(true)
      ) as { [x: string]: string | undefined };
      const { shouldUpdateAppState, shouldUpdateUrl, yearToSet } =
        getShouldUpdateSearchYear({
          mctCurrentPlanYear,
          yearInAppState,
          yearInUrl,
          yearPart,
        });
      if (yearToSet) {
        const shouldPreserveLanguageInUrl =
          languageInUrl &&
          [UserLanguage.English, UserLanguage.Spanish].includes(
            languageInUrl as UserLanguage
          );
        if (shouldUpdateAppState) {
          dispatch({ type: ActionType.UPDATE_YEAR, payload: yearToSet });
        }
        if (shouldUpdateUrl) {
          uri.setQuery(ManagedQueryParameters.YEAR, yearToSet);
        }
        if (shouldPreserveLanguageInUrl) {
          uri.setQuery(ManagedQueryParameters.LANG, languageInUrl);
        }
        if (shouldUpdateUrl || shouldPreserveLanguageInUrl) {
          location.search = uri.search();
          location.hash = new URI().hash(hash || "").hash();
          history.replace(location);
        }
      }
    },
    [dispatch, history, yearInAppState, hash]
  );

  /**
   * Synchronizes language in URL, `AppState`, and `i18n` helpers for DS components
   * (so OOB translations for components should work with our translation system)
   */
  const handleAppLanguage = useCallback(
    (location: RouteComponentProps["location"]): void => {
      const uri = new URI(location.search);
      const { lang: languageInUrl } = parseSearchParams(uri.search(true)) as {
        [x: string]: string | undefined;
      };
      const cmsdsLanguage = getCmsdsLanguage();
      const htmlLanguage = document.documentElement.getAttribute("lang");
      if (
        languageInUrl &&
        [UserLanguage.English, UserLanguage.Spanish].includes(
          languageInUrl as UserLanguage
        )
      ) {
        if (languageInAppState !== languageInUrl) {
          dispatch({
            type: ActionType.UPDATE_LANGUAGE,
            payload: languageInUrl,
          });
        }
        if (cmsdsLanguage !== languageInUrl) {
          setCmsdsLanguage(languageInUrl as CmsdsLanguage);
        }
        if (htmlLanguage !== languageInUrl) {
          document.documentElement.setAttribute("lang", languageInUrl);
        }
      } else {
        uri.setQuery(ManagedQueryParameters.LANG, languageInAppState);
        location.search = uri.search();
        location.hash = new URI().hash(hash || "").hash();
        history.replace(location);
      }
    },
    [dispatch, history, languageInAppState, hash]
  );

  /**
   * Watch changes to yearPart and query params and update values in app state and
   * the URL, accordingly
   */
  useEffect(() => {
    const ypFlagsAreDefined = Object.values(
      getYearPartInfoFromYearPart(yearPart)
    ).every(val => val !== undefined);
    if (!ypFlagsAreDefined) {
      return;
    }
    handleAppYear({
      location,
      mctCurrentPlanYear,
      yearPart,
    });
    // The way this hook is structured, we are modifying `location` directly.
    // For this reason, currently, `location` needs to be a dependency on the effects
    // or more accurately these memoized helpers, but adding it causes issues
    // (using up of the JS memory heap, tests never finishing).
    // We should look into using URI to take the existing pathname (new URI(pathname))
    // adding/updating the query (`newLocation.addSearch(...)`), and then passing that to
    // `history.replace` (but this will require ensuring that stringified query params are
    // combined and not replaced)
    // @see MCT-9130
    // ! TODO: See MCT-11398
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This error has been grandfathered in and needs to be fixed.
  }, [handleAppYear, mctCurrentPlanYear, search, yearPart]);

  useEffect(() => {
    handleAppLanguage(location);
    // @see the note in the other effect helper
    // ! TODO: See MCT-11398
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This error has been grandfathered in and needs to be fixed.
  }, [search, handleAppLanguage]);
};
